/**
 * Host behavior preserved by this wrapper:
 * service subjects open dialogs; uniqueId gates every dialog except contact,
 * done, contact info, and apply. Embedded layout reads the width query.
 * Search appends dialogs to body. Shutdown closes all dialogs and refreshes once.
 */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerAnalyticsService } from '../../../choosen-history/services';
import { CategoryTag } from '@App/common/models';
import {
  BusinessService,
  UserCommonApiService,
  VCardService
} from '@App/common/services';
import { saveAs } from 'file-saver';
import {
  PitchApiService,
  PitchCardModalsWrapperService
} from '@App/modules/pitch-card-shared/services';
import { ScreenSizeService } from '@Shared/services';
import { ActivatedRoute, Router } from '@angular/router';
import { PitchCardTypeEnum } from '@App/common/enums';
import { PitchCardVideoPlayerModalComponent } from './modals/pitchcard-video-player-modal/pitchcard-video-player-modal.component';
import { PitchCardContactDialogsComponent } from './modals/pitchcard-contact-dialogs/pitchcard-contact-dialogs.component';
import { PitchCardVideoReviewsModalComponent } from './modals/pitchcard-video-reviews-modal/pitchcard-video-reviews-modal.component';
import { PitchCardMoreInfoModalComponent } from './modals/pitchcard-more-info-modal/pitchcard-more-info-modal.component';
import { PitchCardJobModalsComponent } from './modals/pitchcard-job-modals/pitchcard-job-modals.component';
import { PitchCardShareModalComponent } from './modals/pitchcard-share-modal/pitchcard-share-modal.component';
import { PitchCardSelectPlanModalComponent } from './modals/pitchcard-select-plan-modal/pitchcard-select-plan-modal.component';
import { PitchCardSavePocketModalComponent } from './modals/pitchcard-save-pocket-modal/pitchcard-save-pocket-modal.component';
import { PitchCardPhotoGalleryModalComponent } from './modals/pitchcard-photo-gallery-modal/pitchcard-photo-gallery-modal.component';
import { PitchCardResumeModalComponent } from './modals/pitchcard-resume-modal/pitchcard-resume-modal.component';
import { PitchCardLearnAboutMeModalComponent } from './modals/pitchcard-learn-about-me-modal/pitchcard-learn-about-me-modal.component';

@Component({
  selector: 'app-pitchcard-modals-wrapper',
  templateUrl: './pitchcard-modals-wrapper.component.html',
  styleUrls: [
    './pitchcard-modals-wrapper.component.scss',
    '../search-result-thumbnail/search-result-thumbnail.component.scss'
  ],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    PitchCardVideoPlayerModalComponent,
    PitchCardContactDialogsComponent,
    PitchCardVideoReviewsModalComponent,
    PitchCardMoreInfoModalComponent,
    PitchCardJobModalsComponent,
    PitchCardShareModalComponent,
    PitchCardSelectPlanModalComponent,
    PitchCardSavePocketModalComponent,
    PitchCardPhotoGalleryModalComponent,
    PitchCardResumeModalComponent,
    PitchCardLearnAboutMeModalComponent
  ]
})
export class PitchCardModalsWrapperComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() uniqueId: string;
  @ViewChild(PitchCardVideoReviewsModalComponent)
  private reviews?: PitchCardVideoReviewsModalComponent;
  @ViewChild(PitchCardLearnAboutMeModalComponent)
  private learnAboutMe?: PitchCardLearnAboutMeModalComponent;

  currentPitchCardType: string = null;
  mobileMode = false;
  isMobile: boolean;
  isPortraitMode = false;
  moreInfoWorkingHours: {
    openHours: string;
    closeHours: string;
    workingDays: string[];
  }[] = [];
  showMoreInfoDialog = false;
  showJobDetailsDialog = false;
  isJobCard: boolean;
  benefits: CategoryTag[];
  workTypes: any[];
  workTypesList: string;
  locationAddress = '';
  compensationDescription = '';
  showJobRequirementsDialog = false;
  shareBusinessUrl: string = null;
  showShareBusinessDialog = false;
  showContactMeDialog = signal(false);
  showDoneDialog = false;
  shareTitle: string;
  showSaveMobileBusinessDialog = false;
  photoGalleriaImages: { file: string; thumbnail: string }[] = [];
  showPhotoGalleriaDialog = false;
  activeGalleryIndex: number;
  mobilePocketScale = 0.4;
  showSaveBusinessDialog = false;
  detailsPocketModal = false;
  showVideoPlayerDialog = false;
  videoReviewId: string = null;
  showViewReviewsDialog = false;
  showUploadReviewVideoDialog = false;
  showContactInfoDialog = false;
  userName: string;
  showResumeFileDialog = false;
  showApplyJobDialog = false;
  userResumeCard: any;
  scaleFactor = 0.8;
  jobCardScaleFactor = 0.7;
  validToApply = false;
  showApplyJobDialogByLink = false;
  learnAboutMeReviews: any[] = [];
  showLearnAboutMeDialog = false;
  showContactSwapDialog = false;
  showSentDialog = false;
  showSelectPlanDialog = false;
  selectPlanBusinessId: string | number = null;
  googleMapLink: any;
  isTablet: boolean;
  isEmbedded;
  isSearch = false;

  private platformId = inject(PLATFORM_ID);
  private screenSizeService = inject(ScreenSizeService);
  private businessService = inject(BusinessService);
  private vCardService = inject(VCardService);
  private customerAnalyticsService = inject(CustomerAnalyticsService);
  private pitchCardModalsWrapperService = inject(PitchCardModalsWrapperService);
  private router = inject(Router);
  private pitchApiService = inject(PitchApiService);
  private cdr = inject(ChangeDetectorRef);
  private userCommonService = inject(UserCommonApiService);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  private _currentPitchCard: any;

  public get currentPitchCard() {
    return this._currentPitchCard;
  }

  public set currentPitchCard(value) {
    this._currentPitchCard = value;
    if (this.currentPitchCard.address) {
      let query = this.currentPitchCard?.address.trim();

      if (
        !query.includes(this.currentPitchCard?.cityName) &&
        !(
          query.includes(this.currentPitchCard?.stateTbl?.stateName) ||
          query.includes(this.currentPitchCard?.stateCode)
        )
      ) {
        if (this.currentPitchCard?.cityName) {
          query += `, ${this.currentPitchCard?.cityName}`;
        }

        if (this.currentPitchCard?.stateTbl?.stateName) {
          query += `, ${this.currentPitchCard?.stateTbl?.stateName}`;
        }
      }

      if (!query.includes(this.currentPitchCard?.zip)) {
        if (this.currentPitchCard?.zip) {
          query += `, ${this.currentPitchCard?.zip}`;
        }
      }

      this.googleMapLink = `https://www.google.com/maps/search/?api=1&query=${query}`;
    }
  }

  @HostBinding('style.--mobile-pocket-scale') get scale() {
    return this.mobilePocketScale;
  }

  onModalFlag({ name, value }: { name: string; value: boolean }) {
    if (name === 'showContactMeDialog') {
      this.showContactMeDialog.set(value);
      return;
    }
    (this as any)[name] = value;
  }

  onSaveBusinessOpened() {
    this.componentMainVariablesInit();
    this.calculateMobilePocketScale();
  }

  ngOnInit() {
    this.isEmbedded = this.activatedRoute.snapshot.url.some(
      ({ path }) => path === 'embedded-card'
    );
    this.isSearch = this.activatedRoute.snapshot.url.some(
      ({ path }) => path === 'search'
    );
    this.onResized = this.onResized.bind(this);
    this.isMobile = this.screenSizeService.isMobile;
    this.calculateMobileMode();
    this.isTablet = this.screenSizeService.isTablet;
    this.shareTitle = !this.isMobile ? 'Share' : '';

    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('resize', this.onResized);
    }
    this.componentMainVariablesInit();
    this.moreInfoSubscriber();
    this.jobDetailsSubscriber();
    this.shareSubscriber();
    this.saveMobileSubscriber();
    this.contactMeSubscriber();
    this.doneDialogSubscriber();
    this.photoGalleriaSubscriber();
    this.resumeFileSubscriber();
    this.saveBusinessDialogSubscriber();
    this.videoPlayerDialogSubscriber();
    this.videoReviewsDialogSubscriber();
    this.uploadVideoReviewSubscriber();
    this.contactInfoDialogSubscriber();
    this.applyJobNowSubscriber();
    this.jobRequirementSubscriber();
    this.learnAboutMeSubscriber();
    this.contactSwapSubscriber();
    this.selectPlanDialogSubscriber();
    this.shutDownAllModalsSubs();
    this.onResized();
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.onResized);
    }
    this.pitchCardModalsWrapperService.clearCurrentBusiness();
  }

  ngAfterViewInit() {
    if (this.showSaveBusinessDialog) {
      this.onResized();
    }
  }

  onResized() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (window.innerHeight <= 1024 && window.innerWidth < 992) {
      this.jobCardScaleFactor = 0.6;
      this.scaleFactor = 0.5;
    } else {
      this.jobCardScaleFactor = 0.7;
    }

    this.calculateMobileMode();

    if (this.showSaveBusinessDialog) {
      this.calculateMobilePocketScale();
    }
  }

  calculateMobileMode() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.isEmbedded) {
      const hostWidth = Number(
        this.activatedRoute.snapshot.queryParamMap.get('width')
      );
      const layoutWidth = hostWidth > 0 ? hostWidth : window.innerWidth;

      this.isMobile = layoutWidth < 768;
      this.mobileMode = this.isMobile;
      this.isPortraitMode =
        this.isMobile || window.innerHeight > window.innerWidth;
      this.shareTitle = !this.isMobile ? 'Share' : '';
      return;
    }

    if (window.innerWidth < 768) {
      this.mobileMode = true;
    }
    if (window.innerWidth >= 768) {
      this.mobileMode = false;
    }
    this.isMobile = this.screenSizeService.isMobile;
    this.isPortraitMode = window.innerHeight > window.innerWidth;
  }

  calculateMobilePocketScale() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (window.innerWidth < 768) {
      const defaultScale = 0.4;
      const paddings = window.innerWidth < 500 ? 48 : 69;
      const defaultPocketWidth = 165;
      const pocketsInTheRow = window.innerWidth < 560 ? 2 : 3;
      const containerWidth = window.innerWidth - paddings;
      let pocketMargins = 80;

      if (window.innerWidth < 560) {
        pocketMargins = 55;
      }
      if (window.innerWidth < 420) {
        pocketMargins = 80;
      }

      this.mobilePocketScale =
        (((containerWidth - pocketMargins) / pocketsInTheRow) * defaultScale) /
        defaultPocketWidth;
    }
  }

  componentMainVariablesInit() {
    this.currentPitchCard =
      this.pitchCardModalsWrapperService.getCurrentBusiness();
    this.currentPitchCardType = this.currentPitchCard.businessType;

    this.isJobCard =
      this.currentPitchCard.businessType === PitchCardTypeEnum.Job;
    this.benefits = this.currentPitchCard.benefits;
    this.workTypes = this.currentPitchCard.workTypes;
    this.setWorkTypesList();
    this.getLocation();
    this.calculateCompensationDescription();
  }

  moreInfoSubscriber() {
    this.pitchCardModalsWrapperService.showMoreInfoDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.showMoreInfoDialog = value;

          if (value) {
            this.componentMainVariablesInit();
            this.moreInfoDialogVariablesInit();
            if (this.currentPitchCard?.pricingModel) {
              this.editorSetup(
                this.currentPitchCard.pricingModel,
                'e_description_' + this.uniqueId
              );
            } else {
              this.getMoreInfoData();
            }
          }
        }
      });
  }

  getMoreInfoData() {
    this.pitchApiService
      .getMoreInfoData(this.currentPitchCard.id)
      .subscribe(({ data }: any) => {
        this.currentPitchCard.pricingModel = data;
        this.editorSetup(data, 'e_description_' + this.uniqueId);
      });
  }

  moreInfoDialogVariablesInit() {
    this.moreInfoWorkingHours =
      this.pitchCardModalsWrapperService.getFormattedWorkingHours();
  }

  jobDetailsSubscriber() {
    this.pitchCardModalsWrapperService.showJobDetailsDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.showJobDetailsDialog = value;

          if (value) {
            this.componentMainVariablesInit();
            this.jobDetailsDialogVariablesInit();
            setTimeout(() => {
              this.editorSetup(
                this.currentPitchCard?.positionRequirements
                  ? this.currentPitchCard.positionRequirements
                  : '',
                'e_requirements_' + this.uniqueId
              );
              this.editorSetup(
                this.currentPitchCard?.pricingModel
                  ? this.currentPitchCard.pricingModel
                  : '',
                'e_description_' + this.uniqueId
              );
            }, 500);
          }
        }
      });
  }

  jobDetailsDialogVariablesInit() {
    this.getLocation();
    this.calculateCompensationDescription();
  }

  editorSetup(controlValue: any, id: string) {
    if (!controlValue || !isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => {
      this.pitchCardModalsWrapperService.previewHtmlData(controlValue, id);
    });
  }

  shareSubscriber() {
    this.pitchCardModalsWrapperService.showShareDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.showShareBusinessDialog = value;
          if (value) {
            this.calculateMobileMode();
            this.componentMainVariablesInit();
            this.shareDialogVariablesInit();
            this.cdr.detectChanges();
          }
        }
      });
  }

  saveMobileSubscriber() {
    this.pitchCardModalsWrapperService.showSaveMobileDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          const width = Number(
            this.activatedRoute.snapshot.queryParamMap.get('width')
          );
          const isEmbeded = this.activatedRoute.snapshot.url.find(
            ({ path }) => path === 'embedded-card'
          );
          if (width < 500 && isEmbeded) {
            this.downloadEmbeddedContact();
            return;
          }
          this.showSaveMobileBusinessDialog = value;
        }
      });
  }

  contactMeSubscriber() {
    this.pitchCardModalsWrapperService.showContactMeDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.showContactMeDialog.set(value);
      });
  }

  doneDialogSubscriber() {
    this.pitchCardModalsWrapperService.showDoneDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.showDoneDialog = true;
      });
  }

  shareDialogVariablesInit() {
    this.shareBusinessUrl =
      this.pitchCardModalsWrapperService.getShareBusinessUrl();
  }

  setDefaultShareTitle() {
    this.shareTitle = 'Share';
  }

  photoGalleriaSubscriber() {
    this.pitchCardModalsWrapperService.showPhotoGalleria
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.activeGalleryIndex = value.index;
          this.showPhotoGalleriaDialog = value.isOpening;

          if (value) {
            this.componentMainVariablesInit();
            this.photoGalleriaVariablesInit();
          }
        }
      });
  }

  applyJobNowSubscriber() {
    this.pitchCardModalsWrapperService.showApplyJobNowDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.userCommonService.isAuthenticated()) {
          this.currentPitchCard =
            this.pitchCardModalsWrapperService.getCurrentBusiness();
          forkJoin([
            this.userCommonService.checkApplicant(
              this.pitchCardModalsWrapperService.getCurrentBusiness().id
            ),
            this.businessService.getResume()
          ]).subscribe({
            next: ([applicant, resume]) => {
              this.onResized();
              this.validToApply = applicant.data;
              if (resume?.data) {
                this.userResumeCard = resume.data;
              }
              if (
                this.currentPitchCard?.otherApplicationLink &&
                this.currentPitchCard?.requireOtherApplicationMethod
              ) {
                this.pitchCardModalsWrapperService.openLinkInNewTab(
                  this.currentPitchCard.otherApplicationLink
                );
              } else {
                this.showApplyJobDialog = true;
              }
            },
            error: (err) => {
              console.error(err.message);
            }
          });
        } else {
          this.currentPitchCard =
            this.pitchCardModalsWrapperService.getCurrentBusiness();
          this.currentPitchCard.otherApplicationLink
            ? (this.showApplyJobDialogByLink = true)
            : (this.showApplyJobDialog = true);
        }
      });
  }

  jobRequirementSubscriber() {
    this.pitchCardModalsWrapperService.showJobRequirementsDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.currentPitchCard =
            this.pitchCardModalsWrapperService.getCurrentBusiness();
          this.showJobRequirementsDialog = value;
          if (value) {
            setTimeout(() => {
              this.editorSetup(
                this.currentPitchCard.positionRequirements,
                'e_requirements_' + this.uniqueId
              );
            }, 500);
          }
        }
      });
  }

  learnAboutMeSubscriber() {
    this.pitchCardModalsWrapperService.showLearnAboutMeDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.currentPitchCard =
            this.pitchCardModalsWrapperService.getCurrentBusiness();
          this.getMoreInfoData();
          this.customerAnalyticsService
            .getBusinessVideoReviewList(this.currentPitchCard.id, {
              limit: null,
              offset: null
            })
            .subscribe(({ data }) => {
              this.learnAboutMeReviews = data.reviews;
              this.cdr.detectChanges();
            });
          this.showLearnAboutMeDialog = value;
        }
      });
  }

  contactSwapSubscriber() {
    this.pitchCardModalsWrapperService.showContactSwapDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.showContactSwapDialog = value;
          if (value) {
            this.currentPitchCard =
              this.pitchCardModalsWrapperService.getCurrentBusiness();
          }
        }
      });
  }

  selectPlanDialogSubscriber() {
    this.pitchCardModalsWrapperService.showSelectPlanDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((businessId) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.selectPlanBusinessId = businessId;
          this.showSelectPlanDialog = true;
        }
      });
  }

  shutDownAllModalsSubs() {
    this.pitchCardModalsWrapperService.$shutDownAllModal
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (!res.withCheck) {
          this.closeAllModals();
          this.router.navigateByUrl(this.router.url).then();
        } else if (this.hasOpenedModals()) {
          this.closeAllModals();
          this.router.navigateByUrl(this.router.url).then();
        }
      });
  }

  closeAllModals() {
    this.showMoreInfoDialog = false;
    this.showJobDetailsDialog = false;
    this.showJobRequirementsDialog = false;
    this.showShareBusinessDialog = false;
    this.showSaveMobileBusinessDialog = false;
    this.showContactMeDialog.set(false);
    this.showDoneDialog = false;
    this.showPhotoGalleriaDialog = false;
    this.showSaveBusinessDialog = false;
    this.detailsPocketModal = false;
    this.showVideoPlayerDialog = false;
    this.showViewReviewsDialog = false;
    this.showUploadReviewVideoDialog = false;
    this.showContactInfoDialog = false;
    this.showResumeFileDialog = false;
    this.showApplyJobDialog = false;
    this.showApplyJobDialogByLink = false;
    this.showContactSwapDialog = false;
    this.showSentDialog = false;
    this.showSelectPlanDialog = false;
    this.selectPlanBusinessId = null;
  }

  hasOpenedModals() {
    const allModals = [
      this.showMoreInfoDialog,
      this.showJobDetailsDialog,
      this.showJobRequirementsDialog,
      this.showShareBusinessDialog,
      this.showContactMeDialog(),
      this.showDoneDialog,
      this.showPhotoGalleriaDialog,
      this.showSaveBusinessDialog,
      this.detailsPocketModal,
      this.showVideoPlayerDialog,
      this.showViewReviewsDialog,
      this.showUploadReviewVideoDialog,
      this.showContactInfoDialog,
      this.showResumeFileDialog,
      this.showApplyJobDialog,
      this.showApplyJobDialogByLink,
      this.showContactSwapDialog,
      this.showSentDialog,
      this.showSelectPlanDialog,
      this.showSaveMobileBusinessDialog
    ];
    return allModals.find((modal) => modal);
  }

  photoGalleriaVariablesInit() {
    this.photoGalleriaImages =
      this.pitchCardModalsWrapperService.getBusinessImages();
  }

  resumeFileSubscriber() {
    this.pitchCardModalsWrapperService.showResumeFileDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.showResumeFileDialog = value;

          if (value) {
            this.componentMainVariablesInit();
          }
        }
      });
  }

  saveBusinessDialogSubscriber() {
    this.pitchCardModalsWrapperService.showSaveBusinessDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.userCommonService.pendingMethod = 'saveAsFavorite';
          this.showSaveBusinessDialog = value;

          if (value) {
            this.componentMainVariablesInit();
            this.calculateMobilePocketScale();
          }
        }
      });
  }

  videoPlayerDialogSubscriber() {
    this.pitchCardModalsWrapperService.showVideoPlayerDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.showVideoPlayerDialog = value;

          if (value) {
            this.componentMainVariablesInit();
            this.videoReviewId =
              this.currentPitchCard && this.currentPitchCard.videoFileId;
          }
        }
      });
  }

  videoReviewsDialogSubscriber() {
    this.pitchCardModalsWrapperService.showVideoReviewsDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.showViewReviewsDialog = value;

          if (value) {
            this.componentMainVariablesInit();
          }
        }
      });
  }

  uploadVideoReviewSubscriber() {
    this.pitchCardModalsWrapperService.showUploadReviewVideoDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.pitchCardModalsWrapperService.uniqueId === this.uniqueId) {
          this.showUploadReviewVideoDialog = value;
          if (value) {
            this.componentMainVariablesInit();
          }
        }
      });
  }

  reviewVideoInit(videoReviewUrl: string) {
    const videoReviewId = videoReviewUrl.split('/').pop().split('.')[0];
    this.showVideoPlayerDialog = true;
    this.videoReviewId = videoReviewId;
  }

  contactInfoDialogSubscriber() {
    this.pitchCardModalsWrapperService.showContactInfoDialog
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isContactNotified) => {
        this.showContactInfoDialog = isContactNotified;
        if (isContactNotified) {
          this.componentMainVariablesInit();
          setTimeout(() => {
            this.showContactInfoDialog = false;
          }, 3000);
        }

        this.userName = this.currentPitchCard?.businessName
          ? this.currentPitchCard.businessName
          : this.currentPitchCard?.title
            ? this.currentPitchCard.title
            : '';
      });
  }

  setWorkTypesList() {
    this.workTypesList = '';
    if (this.workTypes?.length) {
      this.workTypes.map((type, i) => {
        this.workTypesList +=
          i < this.workTypes.length - 1 ? type.name + ', ' : type.name;
      });
    }
  }

  getLocation() {
    if (this.isJobCard) {
      this.locationAddress = this.businessService.getLocation(
        this.currentPitchCard
      );
    }
  }

  calculateCompensationDescription() {
    if (this.isJobCard) {
      this.compensationDescription = this.currentPitchCard
        ? this.businessService.calculateCompensationDescription(
            this.currentPitchCard
          )
        : '';
      this.compensationDescription =
        this.compensationDescription.trim() !== '' &&
        this.compensationDescription.trim() !== '-'
          ? this.compensationDescription
          : '';
    }
  }

  resetReviewUpload() {
    this.reviews?.resetUpload();
  }

  uploadReviewVideo(file: File) {
    this.reviews?.uploadVideoFile(file);
  }

  closeLearnContactMenu() {
    this.learnAboutMe?.closeContactMenu();
  }

  downloadEmbeddedContact() {
    if (!this.vCardService.vCard) {
      return;
    }

    saveAs(
      this.vCardService.downloadUrl,
      this.vCardService.vCard?.name?.firstNames
        ? this.vCardService.vCard.name.firstNames
        : 'vCard'
    );
    this.vCardService.downloadUrl = '';
    this.businessService.addToContactAnalyticsBeacon(
      this.vCardService.businessDetails.id
    );
    this.openContactSwapDialog();
  }

  openContactSwapDialog() {
    if (this.isEmbedded) {
      return;
    }

    this.currentPitchCard =
      this.pitchCardModalsWrapperService.getCurrentBusiness();
    this.showContactSwapDialog = true;
    this.pitchCardModalsWrapperService.enableContactSwapDialog();
  }
}
